<template>
    <div 
         class="application-page flex flex-col flex-grow justify-center container"
         >
        <Toast position="top-center" />
        <div class="page-content">
            <h2 class="page-title mb-4" v-if="application">
                {{ $t(`lib_my_application`) }} (#{{
                    application.applicationId
                }})
                <span 
                      class="job-title text-muted fst-italic"
                      v-tooltip.top.hover="`Job title`"
                      >
                    -
                    {{
                        application.applicationCycle?.jobTitle
                            ? application.applicationCycle?.jobTitle
                            : application.applicationCycle?.name
                    }}
                </span>
            </h2>
            <div class="application-wrapper" v-if="application" aria-busy="false">
                <div class="row">
                    <div class="col-sm-12">
                        <div class="card mb-4">
                            <div 
                                 class="card-header d-flex flex-wrap align-items-center"
                                 >
                                <h4 class="card-title mb-0 me-3">
                                    {{
                                        `${personalData.usualLastName} ${personalData.usualFirstName}`
                                    }}
                                </h4>
                                <div 
                                     class="application-status py-1 text-uppercase fw-bold"
                                     :class="application.status"
                                     >
                                    <font-awesome-icon
                                                       class="application-status-icon me-2 fs-4"
                                                       :class="application.status"
                                                       :icon="applicationStatusIcon(application.status)" 
                                                       />
                                    <span
                                          class="application-status-text"
                                          :class="application.status"
                                          >
                                        {{
                                            $t(
                                                `st_${application.status.toLowerCase()}`
                                            )
                                        }}
                                    </span>
                                </div>
                                <div class="card-actions ms-auto">
                                    <WithdrawApplicationModal
                                                              v-if="application.status !== 'WITHDRAWN'"
                                                              :application-id="applicationId"
                                                              @withdraw-complete="redirectToDashboard"
                                                              />
                                </div>
                            </div>
                            <div class="card-body row">
                                <div class="personal-info-section col-sm-6">
                                    <p class="section-info mb-1">
                                        <span
                                              v-tooltip.top.hover.focus="
                                                $t('lib_birth_date_and_place')
                                            "
                                              >
                                            <font-awesome-icon
                                                               v-if="personalData.birthDate"
                                                               class="me-2"
                                                               icon="fas fa-birthday-cake"
                                                               />
                                        </span>
                                        <span
                                              v-if="personalData.birthDate"
                                              class="birth-date"
                                              >
                                            {{
                                                formatDate(
                                                    personalData.birthDate
                                                )
                                            }}
                                        </span>
                                        <span 
                                              v-if="personalData.birthCity"
                                              class="birth-city"
                                              >
                                            - {{ personalData.birthCity }}
                                        </span>
                                        <span 
                                              class="birth-country"
                                              v-if="
                                                personalData.birthIsoCountryCode
                                            "
                                              >,
                                            {{
                                                countryLabel(
                                                    personalData.birthIsoCountryCode
                                                )
                                            }}
                                        </span>
                                    </p>
                                    <p class="section-info">
                                        <span 
                                              v-tooltip.top.hover.focus="
                                                $t('lib_nationality')
                                            "
                                              >
                                            <font-awesome-icon icon="far fa-flag" class="me-2"/>
                                        </span>
                                        <span
                                              v-if="
                                                personalData.natIsoCountryCode
                                            "
                                              class="nationality"
                                              >
                                            {{
                                                countryLabel(
                                                    personalData.natIsoCountryCode
                                                )
                                            }}
                                        </span>
                                        <span
                                              v-if="
                                                personalData.nat2IsoCountryCode
                                            "
                                              class="2nd-nationality"
                                              >
                                            ,
                                            {{
                                                countryLabel(
                                                    personalData.nat2IsoCountryCode
                                                )
                                            }}
                                        </span>
                                    </p>
                                    <p 
                                       class="section-info"
                                       v-if="personalData.gender"
                                       >
                                        <span
                                              v-tooltip.top.hover.focus="
                                                $t('lib_gender')
                                            "
                                              >
                                            <font-awesome-icon
                                                               icon="far fa-venus-mars"
                                                               class="me-2"
                                                               />
                                        </span>
                                        {{ getGenderInfo() }}
                                    </p>
                                    <p class="section-info">
                                        <span
                                              v-tooltip.top.hover.focus="
                                                $t('lib_address')
                                            "
                                              >
                                            <font-awesome-icon
                                                               icon="far fa-address-card"
                                                               class="me-2"
                                                               />
                                        </span>
                                        {{
                                            `${residence.adStreet},
                                            ${residence.adPostalCode},
                                            ${residence.adCity},
                                            ${countryLabel(
                                                residence.adIsoCountryCode
                                            )}`
                                        }}
                                    </p>
                                </div>
                                <div class="col-sm-6 text-end">
                                    <p class="section-info">
                                        <span
                                              v-tooltip.top.hover.focus="
                                                $t('lib_email')
                                            "
                                              >
                                            <font-awesome-icon icon="fas fa-envelope" class="me-2"/>
                                        </span>
                                        {{ personalData.email }}
                                    </p>
                                    <p class="section-info" v-if="personalData.secondaryEmail">
                                        <span
                                              v-tooltip.top.hover.focus="
                                                $t('lib_secondary_email')
                                            "
                                              >
                                            <font-awesome-icon icon="fas fa-envelope" class="me-2"/>
                                        </span>
                                        {{ personalData.secondaryEmail }}
                                    </p>
                                    <p class="section-info">
                                        <span 
                                              v-tooltip.top.hover.focus="
                                                $t('lib_phone')
                                            "
                                              >
                                            <font-awesome-icon icon="fas fa-phone"  class="me-2"/>
                                        </span>
                                        {{ personalData.phoneNumber }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div 
                         :class="{'mb-4': true,'col-lg-6': !isCaTaType, 'col-lg-12': isCaTaType}"
                         v-if="study.studies || language?.languages"
                         >
                        <div class="card education-data-card h-100">
                            <div class="card-body">
                                <h4 class="card-title">
                                    <font-awesome-icon icon="fas fa-user-graduate" class="me-2"/>
                                    {{ $t('lib_education') }}
                                </h4>

                                <section class="education-info-section">
                                    <!-- <p class="section-title">Education</p> -->
                                    <div 
                                         class="education-section-info mb-2"
                                         v-for="(study, index) in study.studies"
                                         :key="index"
                                         >
                                        <p class="section-info">
                                            <b>{{ $t('lib_org_name') }}:</b>
                                            {{ study.schoolName }}
                                        </p>
                                        <p class="section-info">
                                            <b>{{ $t('lib_from') }}: </b>
                                            <template
                                                      v-if="study.studyStartDate"
                                                      >
                                                {{
                                                    formatDate(
                                                        study.studyStartDate
                                                    )
                                                }}
                                            </template>
                                        </p>
                                        <p class="section-info">
                                            <b>{{ $t('lib_to') }}: </b>
                                            <template v-if="study.studyEndDate">
                                                {{
                                                    formatDate(
                                                        study.studyEndDate
                                                    )
                                                }}
                                            </template>
                                        </p>
                                        <p class="section-info">
                                            <b>{{ $t('lib_edu_date') }}: </b>
                                            <template
                                                      v-if="study.graduationDate"
                                                      >
                                                {{
                                                    formatDate(
                                                        study.graduationDate
                                                    )
                                                }}
                                            </template>
                                        </p>
                                        <p class="section-info">
                                            <b>{{ $t('lib_diploma') }}:</b>
                                            {{ study.diploma }}
                                        </p>
                                        <p class="section-info">
                                            <b>{{ $t('lib_edu_level') }}:</b>
                                            {{
                                                studyLevelLabel(
                                                    study.studyLevel
                                                )
                                            }}
                                        </p>
                                        <p class="section-info">
                                            <b>{{ $t('lib_edu_field') }}:</b>
                                            {{
                                                studyFieldLabel(study.studyType)
                                            }}
                                        </p>
                                        <p class="section-info">
                                            <b>{{ $t('lib_country') }}:</b>
                                            {{
                                                countryLabel(
                                                    study.studyCountryCode
                                                )
                                            }}
                                        </p>
                                        <p
                                           class="section-info"
                                           v-if="
                                                study?.comment &&
                                                study?.comment?.length
                                            "
                                           >
                                            <b>{{ $t('lib_comments') }}:</b>
                                            {{ study?.comment }}
                                        </p>
                                        <div 
                                             class="section-info uploaded-document-wrapper"
                                             v-if="
                                                study?.documentId &&
                                                study?.document
                                            "
                                             >
                                            <div 
                                                 class="uploaded-document mt-2 mb-4 d-flex align-items-center"
                                                 >
                                                <Avatar
                                                        shape="circle"
                                                        size="large"
                                                        class="bg-light border document-icon me-3 p-3"
                                                        v-tooltip.top.hover.focus="
                                                        $t('lib_doc_download')
                                                    "
                                                        @click="
                                                        downloadDoc(
                                                            study?.document
                                                        )
                                                    "
                                                        :icon="
                                                        fileIcon(
                                                            study?.document
                                                                ?.extension
                                                        )
                                                    "
                                                        />
                                                <div
                                                     class="document-details me-lg-3 me-xl-5"
                                                     >
                                                    <p
                                                       @click="
                                                            downloadDoc(
                                                                study.document
                                                            )
                                                        "
                                                       class="fw-bold mb-0 text-uppercase document-name cursor-pointer"
                                                       v-tooltip.top.hover.focus="
                                                            $t(
                                                                'lib_doc_download'
                                                            )
                                                        "
                                                       >
                                                        {{
                                                            study.document.name
                                                        }}
                                                    </p>
                                                    <p
                                                       v-tooltip.top.hover.focus="
                                                            $t(
                                                                'lib_upload_date'
                                                            )
                                                        "
                                                       class="mb-0 small fst-italic text-muted"
                                                       >
                                                        {{
                                                            formatDate(
                                                                study.document
                                                                    .uploadDate
                                                            )
                                                        }}
                                                    </p>
                                                </div>
                                                <button
                                                        type="button"
                                                        class="download-document-btn btn btn-text px-6 py-1 ms-auto"
                                                        @click="
                                                        downloadDoc(
                                                            study.document
                                                        )
                                                    "
                                                        v-tooltip.top.hover.focus="
                                                        $t('lib_doc_download')
                                                    "
                                                        :aria-label="$t('lib_doc_download')"
                                                        >
                                                    <font-awesome-icon
                                                                       icon="far fa-download"
                                                                       />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                                <section 
                                         class="language-info-section table-responsive"
                                         >
                                    <!-- <p class="section-title">Language(s)</p> -->
                                    <table class="table mb-0">
                                        <thead>
                                            <tr>
                                                <th scope="col">
                                                    {{ $t('lib_languages') }}
                                                </th>
                                                <th
                                                    class="text-center"
                                                    scope="col"
                                                    >
                                                    {{ $t('lang_listening') }}
                                                </th>
                                                <th 
                                                    class="text-center"
                                                    scope="col"
                                                    >
                                                    {{ $t('lang_reading') }}
                                                </th>
                                                <th
                                                    class="text-center"
                                                    scope="col"
                                                    >
                                                    {{ $t('lang_interaction') }}
                                                </th>
                                                <th
                                                    class="text-center"
                                                    scope="col"
                                                    >
                                                    {{ $t('lang_production') }}
                                                </th>
                                                <th 
                                                    class="text-center"
                                                    scope="col"
                                                    >
                                                    {{ $t('lang_writing') }}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <template
                                                      v-for="(
                                                    language, index
                                                ) in language?.languages"
                                                      >
                                                <tr
                                                    :key="index"
                                                    v-if="language.langCode"
                                                    >
                                                    <th scope="language">
                                                        {{
                                                            languageLabel(
                                                                language.langCode
                                                            )
                                                        }}
                                                    </th>
                                                    <td class="text-center">
                                                        {{
                                                            language.understOralCode
                                                        }}
                                                    </td>
                                                    <td class="text-center">
                                                        {{
                                                            language.understReadCode
                                                        }}
                                                    </td>
                                                    <td class="text-center">
                                                        {{
                                                            language.speakConversCode
                                                        }}
                                                    </td>
                                                    <td class="text-center">
                                                        {{
                                                            language.speakExpressCode
                                                        }}
                                                    </td>
                                                    <td class="text-center">
                                                        {{
                                                            language.writtenCode
                                                        }}
                                                    </td>
                                                </tr>
                                            </template>
                                        </tbody>
                                    </table>
                                </section>
                            </div>
                        </div>
                    </div>
                    <div 
                         class="col-sm-6 mb-4"
                         v-if="
                            !isCaTaType &&
                           ( experience?.experiences ||
                            euExperience?.hasEUExperience)
                        "
                         >
                        <div class="card experience-data-card h-100">
                            <div class="card-body">
                                <h4 class="card-title">
                                    <font-awesome-icon icon="fad fa-user-tie" class="me-2"/>
                                    {{ $t('lib_prof_experience') }}
                                </h4>
                                <section class="experience-info-section">
                                    <!-- <p class="section-title">
                                        Professional experience
                                    </p> -->
                                    <div
                                         class="experience-section-info mb-2"
                                         v-for="(
                                            experience, index
                                        ) in experience.experiences"
                                         :key="index"
                                         >
                                        <p class="section-info">
                                            <b>{{ $t('lib_org_name') }}:</b>
                                            {{ experience.organisationName }}
                                        </p>
                                        <p class="section-info">
                                            <b>{{ $t('lib_position') }}:</b>
                                            {{ experience.experienceType }}
                                        </p>
                                        <p class="section-info">
                                            <b>{{ $t('lib_duties') }}:</b>
                                            {{ experience.occupation }}
                                        </p>
                                        <p class="section-info">
                                            <b>{{ $t('lib_from') }}:</b>
                                            {{
                                                formatDate(
                                                    experience.experienceStartDate
                                                )
                                            }}
                                            <b>{{ $t('lib_to') }}: </b>

                                            <template
                                                      v-if="
                                                    experience.experienceEndDate
                                                "
                                                      >
                                                {{
                                                    formatDate(
                                                        experience.experienceEndDate
                                                    )
                                                }}
                                                ({{ $t('lib_approximately') }}
                                                {{
                                                    periodBetweenDates(
                                                        experience.experienceStartDate,
                                                        experience.experienceEndDate,
                                                        experience.ongoing
                                                    )
                                                }})
                                            </template>
                                            <!-- <template v-if="experience.ongoing">
                                                {{ $t("lib_ongoing") }}
                                            </template> -->
                                        </p>
                                        <p class="section-info">
                                            <b>{{ $t('lib_country') }}:</b>
                                            {{
                                                countryLabel(
                                                    experience.experienceCountryCode
                                                )
                                            }}
                                        </p>
                                        <p
                                           class="section-info"
                                           v-if="
                                                experience?.comment &&
                                                experience?.comment?.length
                                            "
                                           >
                                            <b>{{ $t('lib_comments') }}:</b>
                                            {{ experience?.comment }}
                                        </p>
                                        <div
                                             class="section-info uploaded-document-wrapper"
                                             v-if="
                                                experience.documentId &&
                                                experience.document
                                            "
                                             >
                                            <div 
                                                 class="uploaded-document mt-2 mb-4 d-flex align-items-center"
                                                 >
                                                <Avatar
                                                        shape="circle"
                                                        size="large"
                                                        class="bg-light border document-icon me-3 p-3"
                                                        v-tooltip.top.hover.focus="
                                                        $t('lib_doc_download')
                                                    "
                                                        @click="
                                                        downloadDoc(
                                                            experience.document
                                                        )
                                                    "
                                                        :icon="
                                                        fileIcon(
                                                            experience.document
                                                                .extension
                                                        )
                                                    "
                                                        :aria-label="$t('lib_doc_download')"
                                                        />
                                                <div 
                                                     class="document-details me-lg-3 me-xl-5"
                                                     >
                                                    <p
                                                       @click="
                                                            downloadDoc(
                                                                experience.document
                                                            )
                                                        "
                                                       aria-role="button"
                                                       :aria-label="$t('lib_doc_download')"
                                                       class="fw-bold mb-0 text-uppercase document-name"
                                                       >
                                                        {{
                                                            experience.document
                                                                .name
                                                        }}
                                                    </p>
                                                    <p
                                                       v-tooltip.top.hover.focus="
                                                            $t(
                                                                'lib_upload_date'
                                                            )
                                                        "
                                                       :aria-label="$t('lib_upload_date')"
                                                       class="mb-0 small fst-italic text-muted"
                                                       >
                                                        {{
                                                            formatDate(
                                                                experience
                                                                    .document
                                                                    .uploadDate
                                                            )
                                                        }}
                                                    </p>
                                                </div>
                                                <button 
                                                        type="button"
                                                        class="download-document-btn btn btn-text px-6 py-1 ms-auto"
                                                        @click="
                                                        downloadDoc(
                                                            experience.document
                                                        )
                                                    "
                                                        v-tooltip.top.hover.focus="
                                                        $t('lib_doc_download')
                                                    "
                                                        >
                                                    <font-awesome-icon 
                                                                       icon="far fa-download"
                                                                       />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                                <section
                                         class="eu-experience-info-section mt-4"
                                         v-if="euExperience?.hasEUExperience"
                                         >
                                    <h4>
                                        <font-awesome-icon icon="fad fa-university" class="me-2"/>
                                        {{ $t('lib_traineeship_eu') }}
                                    </h4>
                                    <div
                                         class="eu-experience-section-info mb-2"
                                         >
                                        <p class="section-info">
                                            <b>{{ $t('lib_org_name') }}:</b>
                                            {{
                                                europeanInstitutionsLabel(
                                                    euExperience.euOrganisationCode
                                                )
                                            }}
                                        </p>
                                        <p class="section-info">
                                            <b>{{ $t('lib_from') }}:</b>
                                            {{
                                                formatDate(
                                                    euExperience.euExperienceStartDate
                                                )
                                            }}
                                            <b>{{ $t('lib_to') }}: </b>
                                            <template
                                                      v-if="
                                                    euExperience.euExperienceEndDate
                                                "
                                                      >
                                                {{
                                                    formatDate(
                                                        euExperience.euExperienceEndDate
                                                    )
                                                }}
                                                ({{ $t('lib_approximately') }}
                                                {{
                                                    periodBetweenDates(
                                                        euExperience.euExperienceStartDate,
                                                        euExperience.euExperienceEndDate,
                                                        false
                                                    )
                                                }}
                                                )
                                            </template>
                                        </p>
                                        <p
                                           class="section-info"
                                           v-if="
                                                euExperience?.comment &&
                                                euExperience?.comment?.length
                                            "
                                           >
                                            <b>{{ $t('lib_comments') }}:</b>
                                            {{ euExperience?.comment }}
                                        </p>
                                        <div 
                                             class="section-info uploaded-document-wrapper"
                                             v-if="
                                                euExperience?.documentId &&
                                                euExperience?.document
                                            "
                                             >
                                            <div 
                                                 class="uploaded-document mt-2 mb-4 d-flex align-items-center"
                                                 >
                                                <Avatar 
                                                        shape="circle"
                                                        size="large"
                                                        class="bg-light border document-icon me-3 p-3"
                                                        v-tooltip.top.hover.focus="
                                                        $t('lib_doc_download')
                                                    "
                                                        aria-role="button"
                                                        :aria-label="$t('lib_doc_download')"
                                                        @click="
                                                        downloadDoc(
                                                            euExperience.document
                                                        )
                                                    "
                                                        :icon="
                                                        fileIcon(
                                                            euExperience
                                                                .document
                                                                .extension
                                                        )
                                                    "
                                                        />
                                                <div
                                                     class="document-details me-lg-3 me-xl-5"
                                                     >
                                                    <p
                                                       v-tooltip.top.hover.focus="
                                                            $t(
                                                                'lib_doc_download'
                                                            )
                                                        "
                                                       aria-role="button"
                                                       @click="
                                                            downloadDoc(
                                                                euExperience.document
                                                            )
                                                        "
                                                       class="fw-bold mb-0 text-uppercase document-name"
                                                       >
                                                        {{
                                                            euExperience
                                                                .document.name
                                                        }}
                                                    </p>
                                                    <p 
                                                       v-tooltip.top.hover.focus="
                                                            $t(
                                                                'lib_upload_date'
                                                            )
                                                        "
                                                       class="mb-0 small fst-italic text-muted"
                                                       >
                                                        {{
                                                            formatDate(
                                                                euExperience
                                                                    .document
                                                                    .uploadDate
                                                            )
                                                        }}
                                                    </p>
                                                </div>
                                                <button 
                                                        type="button"
                                                        class="download-document-btn btn btn-text px-6 py-1 ms-auto"
                                                        @click="
                                                        downloadDoc(
                                                            euExperience.document
                                                        )
                                                    "
                                                        v-tooltip.top.hover.focus="
                                                        $t('lib_doc_download')
                                                    "
                                                        >
                                                    <font-awesome-icon 
                                                                       icon="far fa-download" 
                                                                       />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm mb-4">
                        <div class="card application-data-card h-100">
                            <div class="card-body">
                                <h4 class="card-title">
                                    <font-awesome-icon icon="fas fa-user-cog"  class="me-2"/>
                                    {{ isCaTaType ? $t('lib_step_motivation_strenghts') : $t('lib_step_application') }}
                                </h4>
                                <section class="units-info-section">
                                    <!-- <p class="section-title">Preferred unit</p> -->
                                    <div
                                         class="section-info"
                                         v-for="(
                                            interest, index
                                        ) in interest?.interests"
                                         :key="index"
                                         >
                                        <p class="section-info mt-2">
                                            <b>{{ $t('lib_preference') }}:</b>
                                            {{
                                                unitDisplayName(interest.unitId)
                                            }}
                                            <b v-if="applicationTypeIsTrainee">{{ $t('lib_description_position') }}:</b>
                                            <span class="px-1" :key="index2" v-tooltip.top.hover.focus="$t('lib_click_details')"
                                                    @click="showTrainsheepInfo($event, unitPos)"
                                                    v-for="(unitPos, index2) in interest.unitPositions">
                                                  <font-awesome-icon  icon="fas fa-info-circle fs-4 me-3 pointer" />
                                            </span>       
                                        </p>
                                        <p 
                                           class="section-info mt-2"
                                           v-if="
                                                interest?.comments &&
                                                interest?.comments?.length
                                            "
                                           >
                                            <b>{{ $t('lib_motivations') }}:</b>
                                            {{ interest.comments }}
                                        </p>
                                    </div>
                                    <p
                                       class="section-info"
                                       v-if="
                                            interest?.comment &&
                                            interest?.comment?.length
                                        "
                                       >
                                        <b>{{ $t('lib_comments') }}:</b>
                                        {{ interest.comment }}
                                    </p>
                                    <p
                                       v-if="isOfficialType"
                                       class="section-info mt-2"
                                       >
                                        <b> {{ $t('lib_cand_type') }}: </b>
                                        {{
                                            eligibilityCategoryDescription(
                                                officialEligibility?.category
                                            )
                                        }}
                                    </p>
                                    <p
                                       v-if="hasApplicationGrade"
                                       class="section-info mt-2">
                                        <b> {{ $t('lib_your_current_grade') }} </b>
                                        {{
                                              officialEligibility?.applicantGrade
                                        }}
                                    </p>
                                    <p v-if="hasCompetitionNo"
                                       class="section-info mt-2">
                                        <b> {{ $t('lib_competition_no') }}: </b>
                                        {{
                                            officialEligibility?.competitionNo
                                        }}
                                    </p>
                                </section>
                                <section
                                         class="skills-info-section"
                                         v-if="!isOfficialType && !isCaTaType"
                                         >
                                    <p class="section-title">
                                        {{ $t('lib_skills') }}
                                    </p>
                                    <p
                                       class="section-info mt-2"
                                       v-if="
                                            otherSkill?.computerSkillsComment &&
                                            otherSkill?.computerSkillsComment
                                                .length
                                        "
                                       >
                                        <b>{{ $t('skill_computer') }}:</b>
                                        {{ otherSkill.computerSkillsComment }}
                                    </p>
                                    <p 
                                       class="section-info mt-2"
                                       v-if="
                                            otherSkill?.organizationalSkillsComment &&
                                            otherSkill
                                                ?.organizationalSkillsComment
                                                .length
                                        "
                                       >
                                        <b>{{ $t('skill_organisation') }}:</b>
                                        {{
                                            otherSkill.organizationalSkillsComment
                                        }}
                                    </p>
                                    <p 
                                       class="section-info mt-2"
                                       v-if="
                                            otherSkill?.socialSkillsComment &&
                                            otherSkill?.socialSkillsComment
                                                .length
                                        "
                                       >
                                        <b>{{ $t('skill_social') }}:</b>
                                        {{ otherSkill.socialSkillsComment }}
                                    </p>
                                    <p
                                       class="section-info mt-2"
                                       v-if="
                                            otherSkill?.technicalSkillsComment &&
                                            otherSkill?.technicalSkillsComment
                                                ?.length
                                        "
                                       >
                                        <b>{{ $t('skill_technical') }}:</b>
                                        {{ otherSkill.technicalSkillsComment }}
                                    </p>
                                    <p
                                       class="section-info mt-2"
                                       v-if="
                                            otherSkill?.comment &&
                                            otherSkill?.comment?.length
                                        "
                                       >
                                        <b>{{ $t('lib_comments') }}:</b>
                                        {{ otherSkill.comment }}
                                    </p>
                                </section>
                                <section
                                    class="skills-info-section"
                                    v-if="isCaTaType"
                                >
                                    <p
                                        class="section-info mt-2"
                                        v-if="
                                            otherSkill?.experienceComment &&
                                            otherSkill?.experienceComment
                                                ?.length
                                        "
                                    >
                                        <b>{{ $t('skill_cata_experience') }}:</b>
                                        {{ otherSkill.experienceComment }}
                                    </p>
                                    <p
                                        class="section-info mt-2"
                                        v-if="
                                            otherSkill?.skillsComment &&
                                            otherSkill?.skillsComment
                                                ?.length
                                        "
                                    >
                                        <b>{{ $t('skill_cata_skills') }}:</b>
                                        {{ otherSkill.skillsComment }}
                                    </p>
                                    <p
                                        class="section-info mt-2"
                                        v-if="
                                            otherSkill?.motivationComment &&
                                            otherSkill
                                                ?.motivationComment
                                                ?.length
                                        "
                                    >
                                        <b>{{ $t('skill_cata_motivation') }}:</b>
                                        {{
                                            otherSkill.motivationComment
                                        }}
                                    </p>
                                </section>
                                <section
                                         class="trainings-info-section"
                                         v-if="
                                        trainingDuration?.trainingDurations
                                            ?.length ||
                                        trainingDuration?.comment?.length
                                    "
                                         >
                                    <p class="section-title">
                                        {{ $t('lib_availability') }}
                                    </p>
                                    <p
                                       class="section-info"
                                       v-for="(
                                            trainingDuration, index
                                        ) in trainingDuration?.trainingDurations"
                                       :key="index"
                                       >
                                        {{
                                            formatDate(
                                                trainingDuration.trainingStartDate
                                            )
                                        }}
                                        -
                                        {{
                                            formatDate(
                                                trainingDuration.trainingEndDate
                                            )
                                        }}
                                        - {{ durationLabel(trainingDuration) }}
                                    </p>
                                    <p 
                                       class="section-info mt-2"
                                       v-if="
                                            trainingDuration?.comment &&
                                            trainingDuration?.comment?.length
                                        "
                                       >
                                        <b>{{ $t('lib_comments') }}:</b>
                                        {{ trainingDuration.comment }}
                                    </p>
                                </section>
                            </div>
                        </div>
                    </div>
                    <div
                         class="col-sm-6 mb-4"
                         v-if="
                            showApplicationStatusCard &&
                            application?.positions &&
                            application?.positions.length
                        "
                         >
                        <div class="card application-status-card h-100">
                            <div class="card-body">
                                <h4 class="card-title">
                                    <font-awesome-icon icon="fas fa-heart-rate" class="me-2"/>
                                    {{ $t('lib_step_status') }}
                                </h4>
                                <div class="section-info">
                                    <b>
                                        {{
                                            $t(
                                                `st_${application.status.toLowerCase()}`
                                            )
                                        }}
                                        {{ $t(`lib_by`) }}
                                    </b>
                                    <ul>
                                        <li
                                            v-for="(
                                                position, index
                                            ) in application.positions"
                                            :key="index"
                                            >
                                            {{
                                                unitDisplayName(position.unitId)
                                            }}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row" v-if="specialNeeds">
                    <div class="col-sm mb-4">
                        <div class="card accessibility-data-card h-100">
                            <div class="card-body">
                                <h4 class="card-title">
                                    <font-awesome-icon icon="fas fa-universal-access" class="me-2"/>
                                    {{ $t('lib_step_special_needs') }}
                                </h4>
                                <section class="accessibility-info-section">
                                    <div class="section-info">
                                        <p class="section-info mt-2">
                                            <b>{{ $t('lib_special_needs_question') }} </b>
                                            {{ specialNeeds?.hasSpecialNeeds ? $t('lib_yes') : $t('lib_no') }}
                                        </p>
                                        <p
                                           class="section-info mt-2"
                                           v-if="specialNeeds?.hasSpecialNeeds"
                                           >
                                            <b>{{ $t('lib_comments') }}:</b>
                                            {{ specialNeeds?.description }}
                                        </p>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row mb-4" v-if="hasEligibilityDocuments">
                    <div class="col-sm-12">
                        <div class="card other-documents-card">
                            <div class="card-body">
                                <h4 class="card-title">
                                    <font-awesome-icon icon="far fa-file-invoice" class="me-2"/>
                                    {{ $t('lib_docs_eligibility') }}
                                </h4>
                                <section class="uploaded-documents-section">
                                    <div 
                                         class="section-info uploaded-document-wrapper"
                                         >
                                        <template v-for="(
                                                type, index
                                                ) in eligibilityDocumentTypes"
                                                  :key="index"
                                                  >
                                            <template v-if="getDocumentsByType(type).length">
                                                <div class="d-flex row filtered-documents">
                                                    <div class="mt-3">
                                                        <span
                                                              v-if="
                                                                getDocumentsByType(type)[0]?.documentType?.name
                                                            "
                                                              v-tooltip.top.hover.focus="
                                                                    $t('lib_doc_type')
                                                                "
                                                              class="mb-0"
                                                              >
                                                            {{ $t(getDocumentsByType(type)[0].documentType.name) }}
                                                        </span>
                                                    </div>
                                                    <template
                                                              v-for="(
                                                            document, index
                                                        ) in getDocumentsByType(type)"
                                                              :key="index"
                                                              >
                                                        <div class="uploaded-document uploaded-document-eligibility mt-3 mb-2 d-flex align-items-center col-md-6 col-lg-6 col-xl-4">
                                                            <Avatar
                                                                    shape="circle"
                                                                    size="large"
                                                                    class="bg-light border document-icon me-3 p-3"
                                                                    v-tooltip.top.hover.focus="
                                                                    $t('lib_doc_download')
                                                                "
                                                                    :aria-label="$t('lib_doc_download')"
                                                                    @click="downloadDoc(document)"
                                                                    :icon="
                                                                    fileIcon(document.extension)
                                                                "
                                                                    />
                                                            <div
                                                                 class="document-details me-lg-3 me-xl-5"
                                                                 >
                                                                <p
                                                                   @click="
                                                                        downloadDoc(document)
                                                                    "
                                                                   aria-role="button"
                                                                   :aria-label="$t('lib_doc_download')"
                                                                   class="fw-bold mb-0 text-uppercase document-name cursor-pointer"
                                                                   v-tooltip.top.hover.focus="
                                                                        $t('lib_doc_download')
                                                                    "
                                                                   >
                                                                    {{ document.name }}
                                                                </p>
                                                                <p
                                                                   class="mb-0 small fst-italic text-muted"
                                                                   v-tooltip.top.hover.focus="
                                                                        $t('lib_upload_date')
                                                                    "
                                                                   >
                                                                    {{
                                                                        formatDate(
                                                                            document.uploadDate
                                                                        )
                                                                    }}
                                                                </p>
                                                            </div>
                                                            <button
                                                                    type="button"
                                                                    class="download-document-btn btn btn-text px-6 py-1 ms-auto"
                                                                    @click="downloadDoc(document)"
                                                                    v-tooltip.top.hover.focus="
                                                                    $t('lib_doc_download')
                                                                "
                                                                    >
                                                                <font-awesome-icon icon="far fa-download"/>
                                                            </button>
                                                        </div>
                                                    </template>
                                                </div>
                                            </template>
                                        </template>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row mb-5" v-if="hasSelectionDocuments">
                    <div class="col-sm-12">
                        <div class="card other-documents-card">
                            <div class="card-body">
                                <h4 class="card-title">
                                    <font-awesome-icon icon="far fa-file-invoice" class="me-2"/>
                                    {{ $t('lib_docs_selection') }}
                                </h4>
                                <section class="uploaded-documents-section">
                                    <div 
                                         class="section-info uploaded-document-wrapper row"
                                         >
                                        <template v-for="(
                                            type
                                            ) in selectionDocumentTypes"
                                                  >
                                            <template v-if="getDocumentsByType(type).length">

                                                <template 
                                                          v-for="(
                                                        document, index
                                                    ) in getDocumentsByType(type)"
                                                          :key="index"
                                                          >
                                                    <div 
                                                         class="uploaded-document uploaded-document-selection mt-3 mb-2 d-flex align-items-center col-md-6 col-lg-6 col-xl-4"
                                                         >
                                                        <Avatar
                                                                shape="circle"
                                                                size="large"
                                                                class="bg-light border document-icon me-3 p-3"
                                                                v-tooltip.top.hover.focus="
                                                                $t('lib_doc_download')
                                                            "
                                                                :aria-label="$t('lib_doc_download')"
                                                                @click="downloadDoc(document)"
                                                                :icon="
                                                                fileIcon(document.extension)
                                                            " 
                                                                />
                                                        <div 
                                                             class="document-details me-lg-3 me-xl-5"
                                                             >
                                                            <p
                                                               @click="
                                                                    downloadDoc(document)
                                                                "
                                                               :aria-label="$t('lib_doc_download')"
                                                               aria-role="button"
                                                               class="fw-bold mb-0 text-uppercase document-name cursor-pointer"
                                                               v-tooltip.top.hover.focus="
                                                                    $t('lib_doc_download')
                                                                "
                                                               >
                                                                {{ document.name }}
                                                            </p>
                                                            <p
                                                               v-if="document.documentType"
                                                               v-tooltip.top.hover.focus="
                                                                    $t('lib_doc_type')
                                                                "
                                                               class="mb-0 small fst-italic text-muted"
                                                               >
                                                                {{
                                                                    $t(
                                                                        document
                                                                            .documentType
                                                                            .name
                                                                    )
                                                                }}
                                                            </p>
                                                            <p
                                                               class="mb-0 small fst-italic text-muted"
                                                               v-tooltip.top.hover.focus="
                                                                    $t('lib_upload_date')
                                                                "
                                                               >
                                                                {{
                                                                    formatDate(
                                                                        document.uploadDate
                                                                    )
                                                                }}
                                                            </p>
                                                        </div>
                                                        <button 
                                                                type="button"
                                                                class="download-document-btn btn btn-text px-6 py-1 ms-auto"
                                                                @click="downloadDoc(document)"
                                                                v-tooltip.top.hover.focus="
                                                                $t('lib_doc_download')
                                                            "
                                                                >
                                                            <font-awesome-icon icon="far fa-download"/>
                                                        </button>
                                                    </div>
                                                </template>
                                            </template>
                                        </template>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                </div>
                <slot></slot>
            </div>
            <div v-if="loading" class="card view-application-skeleton mb-5" aria-busy="true">
                <div class="card-header d-flex justify-content-between py-3">
                    <Skeleton width="12rem"></Skeleton>
                    <Skeleton width="8rem"></Skeleton>
                </div>
                <div class="card-body d-flex justify-content-between flex-wrap">
                    <div>
                        <Skeleton width="10rem" class="mb-2"></Skeleton>
                        <Skeleton width="5rem" class="mb-2"></Skeleton>
                        <Skeleton width="7rem" class="mb-2"></Skeleton>
                        <Skeleton width="8rem" class="mb-2"></Skeleton>
                    </div>
                    <div>
                        <Skeleton 
                                  width="14rem"
                                  class="ms-md-auto mb-2"
                                  ></Skeleton>
                        <Skeleton
                                  width="12rem"
                                  class="ms-md-auto mb-2"
                                  ></Skeleton>
                        <div class="d-flex justify-content-between flex-wrap">
                            <Skeleton
                                      width="6rem"
                                      height="2rem"
                                      class="ms-md-auto mb-2"
                                      ></Skeleton>
                            <Skeleton
                                      width="6rem"
                                      height="2rem"
                                      class="ms-md-auto mb-2"
                                      ></Skeleton>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <OverlayPanel @hide="onHidePanel" ref="op">
        <div class="flex flex-column gap-3 w-25rem">
            <div style="width:600px" v-html="trainshipDescription"></div>
        </div>
    </OverlayPanel>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Toast from 'primevue/toast';
import Skeleton from 'primevue/skeleton';
import WithdrawApplicationModal from '@/components/WithdrawApplicationModal.vue';
import Avatar from 'primevue/avatar';
import CommonMixins from '../mixins';
import { parseISO, differenceInMonths, differenceInYears } from 'date-fns';
import OverlayPanel from 'primevue/overlaypanel';

// import Button from "primevue/button";
export default {
    name: 'ApplicationOverview',
    components: {
        Toast,
        Skeleton,
        WithdrawApplicationModal,
        Avatar,
        OverlayPanel
    },
    data() {
        return {
            loading: true,
            application: null,
            applicationId: null,
            euExperience: null,
            experience: null,
            govOfficialType: null,
            interest: null,
            language: null,
            otherSkill: null,
            personalData: null,
            residence: null,
            study: null,
            trainingDuration: null,
            officialEligibility: null,
            showNotes: false,
            notes: '',
            specialNeeds: null,
            eligibilityDocumentTypes: ['CV','MOTIVATION','IDENTITY','STUDY','EXPERIENCE','EUEXPERIENCE','PROOFGRADE','EPSO','OTHER', 'FUNDING', 'OTHER_SUPPORTING_DOCUMENTS'],
            selectionDocumentTypes: ['LEGAL','BANK','CRIMINAL','MEDICAL','INSURANCE', 'ENGAGEMENT', 'IDENTIFICATION_FORM'],
            trainshipDescription: ''
        };
    },
    computed: {
        ...mapGetters([
            'countryLabel',
            'languageLabel',
            'studyFieldLabel',
            'studyLevelLabel',
            'unitDisplayName',
            'europeanInstitutionsLabel',
            'eligibilityCategoryDescription',
            'wizardData',
            'applicationTypeIsTrainee'
        ]),
        showApplicationStatusCard() {
            return (
                this.application.status === 'PRESELECTED' ||
                this.application.status === 'SELECTED'
            );
        },
        isOfficialType() {
            return (
                this.application?.applicationType?.toLowerCase() === 'official'
            );
        },
        isCaTaType() {
            return (
                this.application?.applicationType?.toLowerCase() === 'ca' || this.application?.applicationType?.toLowerCase() === 'ta'
            );
        },
        hasApplicationGrade() {
            const acceptedValues = ['official', 'cert', 'grade'];
            return acceptedValues.includes(this.officialEligibility?.category?.toLowerCase());
        },
        hasCompetitionNo() {
            const acceptedValues = ['epso', 'grade']
            return acceptedValues.includes(this.officialEligibility?.category?.toLowerCase());
        },
        hasEligibilityDocuments(){
            return this.application?.documents?.filter((doc) => {
                return this.eligibilityDocumentTypes.includes(doc?.documentType?.code)
            }).length > 0;
        },
        hasSelectionDocuments(){
            return this.application?.documents?.filter((doc) => {
                return this.selectionDocumentTypes.includes(doc?.documentType?.code);
            }).length > 0;
        }
    },
    methods: {
        ...mapActions([
            'loadAppData',
            'loadApplicationData',
            'downloadDocument',
            'redirectToDashboard',
        ]),
        setApplicationData() {
            this.euExperience = {
                ...this.application?.applicationBlocks?.euExperience,
                document: this.application?.applicationBlocks?.euExperience
                    ?.documentId
                    ? this.sectionDocument(
                          this.application?.applicationBlocks?.euExperience
                              ?.documentId
                      )
                    : null,
            };
            this.experience = {
                ...this.application.applicationBlocks.experience,
                experiences:
                    this.application?.applicationBlocks?.experience?.experiences.map(
                        (experience) => {
                            return {
                                ...experience,
                                document: experience?.documentId
                                    ? this.sectionDocument(
                                          experience?.documentId
                                      )
                                    : null,
                            };
                        }
                    ),
            };
            this.govOfficialType =
                this.application?.applicationBlocks?.govOfficialType;
            this.interest = this.application?.applicationBlocks?.interest;
            this.language = this.application?.applicationBlocks?.language;
            this.otherSkill = this.application?.applicationBlocks?.otherSkill;
            this.personalData =
                this.application?.applicationBlocks?.personalData;
            this.residence = this.application?.applicationBlocks?.residence;
            this.study = this.application?.applicationBlocks?.study;

            this.study = {
                ...this.application?.applicationBlocks?.study,
                studies:
                    this.application?.applicationBlocks?.study?.studies.map(
                        (study) => {
                            return {
                                ...study,
                                document: study?.documentId
                                    ? this.sectionDocument(study?.documentId)
                                    : null,
                            };
                        }
                    ),
            };

            this.trainingDuration =
                this.application?.applicationBlocks?.trainingDuration;
            this.officialEligibility =
                this.application?.applicationBlocks?.officialEligibility;
            this.specialNeeds = 
                this.application?.applicationBlocks?.specialNeeds;
        },
        periodBetweenDates(startDate, endDate, isOngoing) {
            const a = isOngoing ? new Date() : parseISO(endDate);
            const b = parseISO(startDate);
            if (differenceInMonths(a, b) >= 12) {
                return `${differenceInYears(a, b)} ${
                    differenceInYears(a, b) > 1
                        ? this.$i18n.t('lib_years')
                        : this.$i18n.t('lib_year')
                }`;
            } else {
                return `${differenceInMonths(a, b)} ${this.$i18n.t('lib_months')}`;
            }
        },
        durationLabel(trainingObj) {
            if (trainingObj.durationType === 'm') {
                return `${trainingObj.durationMonths} ${this.$i18n.t(
                    'lib_months'
                )}`;
            }
        },
        addNote() {
            this.showNotes = true;
        },
        saveNotes() {
            //send data to backend;
        },
        applicationStatusIcon(statusCode) {
            let statusIcon;
            switch (statusCode) {
                case 'REG':
                    statusIcon = 'fas fa-check-double';
                    break;
                case 'SELECTED':
                    statusIcon = 'fas fa-trophy';
                    break;
                case 'DRAFT':
                    statusIcon = 'fas fa-pencil';
                    break;
                case 'DELETED':
                    statusIcon = 'fas fa-trash';
                    break;
                case 'PRESELECTED':
                    statusIcon = 'fas fa-clipboard-check';
                    break;
                case 'RESERVE':
                    statusIcon = 'fas fa-clipboard-list';
                    break;
                case 'WITHDRAWN':
                    statusIcon = 'fas fa-user-slash';
                    break;
                default:
                    statusIcon = 'fas fa-check';
                    break;
            }
            return statusIcon;
        },
        fileIcon(extension) {
            let fileIcon;
            switch (extension) {
                case '.pdf':
                    fileIcon = 'pi pi-file-pdf';
                    break;
                case '.jpg':
                    fileIcon = 'pi pi-image';
                    break;
                default:
                    fileIcon = 'pi pi-file';
                    break;
            }
            return fileIcon;
        },
        downloadDoc(document) {
            // this methods refers to the event triggered by the download btn;
            this.downloadDocument(document.id).then(
                (res) => {
                    this.downloadFile(res, document.name);
                    this.$toast.add({
                        severity: 'success',
                        detail: this.$t('lib_action_success', [
                            this.$t('lib_doc_download'),
                        ]), //"Document downloaded succesfully!",
                        life: 3000,
                    });
                },
                (err) => {
                    console.error(err);
                    // here should be error notification;
                    this.$toast.add({
                        severity: 'error',
                        detail: this.$t('lib_action_failed', [
                            this.$t('lib_doc_download'),
                        ]), // "There was a problem downloading your file, please try again later.",
                        life: 3000,
                    });
                }
            );
        },
        onHidePanel() {
            if (this.ev) {
                this.$refs.op.show(this.ev);
                this.ev = null;
                this.unitPos = this.crtunitPos;
                this.trainshipDescription = this.crtunitPos.description;
            } else {
                this.unitPos = null;
            }
        },
        showTrainsheepInfo(event, unitPos) {
            const pop = this.$refs.op;
            if (this.unitPos == unitPos) {
                this.unitPos = null;
                pop.hide();
            } else {
                if (this.$refs.op.visible) {

                    this.crtunitPos = unitPos;
                    this.ev = event;
                    pop.hide();
                } else {
                    this.unitPos = unitPos;
                    this.trainshipDescription = unitPos.description
                    pop.show(event);
                }
            }
        },
        downloadFile(res, name) {
            // this method refers to the event of downloading the file after we recieve the response from the api;
            let fileBlob = window.URL.createObjectURL(new Blob([res.data])),
                virtualFile = document.createElement('a');

            virtualFile.href = fileBlob;
            virtualFile.setAttribute('download', name);
            document.body.appendChild(virtualFile);
            virtualFile.click();
        },
        sectionDocument(documentId) {
            console.log(
                'ddaree',
                this.application.documents.find((doc) => doc.id === documentId)
            );
            return this.application.documents.find(
                (doc) => doc.id === documentId
            );
        },
        getDocumentsByType(type){
            return this.application?.documents?.filter(doc => doc?.documentType?.code === type);
        },
        getGenderInfo() {
            let gender = "";
            if (this.personalData.gender && this.personalData.identifyAs) {
                gender = this.personalData.gender + " - " + this.personalData.identifyAs;
            }
            else {
                gender = this.personalData.gender;
            }

            return gender;
        }
    },
    created() {
        this.loadAppData();
    },
    mounted() {
        let urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('id')) {
            this.applicationId = urlParams.get('id');
            if (!this.wizardData){
                this.loadApplicationData({
                    id: this.applicationId,
                    includeValidationErrors: false,
                }).then(
                    (data) => {
                        this.loading = false;
                        this.application = data.model;
                        this.setApplicationData();
                        // console.log(this.application);
                    },
                    (err) => {
                        console.error(err);
                        // here should be error notification;
                        this.$toast.add({
                            severity: 'error',
                            detail: this.$t('lib_application_error'), //  "Application not found! You will be redirected to Dashboard",
                            life: 3000,
                        });
                        if (err.status === 404) {
                            location.href = '/';
                        }
                    }
                );
            } else {
                this.application = this.wizardData;
                this.loading = false;
                this.setApplicationData()
            }
            
        } else {
            this.$toast.add({
                severity: 'error',
                detail: this.$t('lib_application_error'), //  "Application not found! You will be redirected to Dashboard",
                life: 3000,
            });
            history.back();
        }
    },
    mixins: [CommonMixins],
};
</script>